import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { HelpCircle, TrendingUp, TrendingDown, Minus, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { MONTHS } from "@/lib/config";
import { getStatusColor, getTrendArrow } from "@/lib/targets";

export const PeriodSelector = ({ year, setYear, month, setMonth, years = [2026, 2025, 2024], view = "mensuel", setView, quarter, setQuarter }) => {
  // Si pas de setView, mode simple (ancien comportement)
  if (!setView) {
    return (
      <div className="flex items-center gap-3">
        <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
          <SelectTrigger data-testid="month-selector" className="w-36 bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MONTHS.slice(1).map((m, idx) => (
              <SelectItem key={idx + 1} value={String(idx + 1)}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger data-testid="year-selector" className="w-24 bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {years.map(y => (
              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  // Mode avancé avec vues Mensuel/Trimestriel/Annuel
  const QUARTERS = [
    { id: 1, label: "T1 (Jan-Mar)" },
    { id: 2, label: "T2 (Avr-Juin)" },
    { id: 3, label: "T3 (Juil-Sep)" },
    { id: 4, label: "T4 (Oct-Déc)" }
  ];

  return (
    <div className="flex flex-col gap-2 bg-white rounded-lg border border-slate-200 p-2 sm:flex-row sm:items-center sm:gap-3">
      {/* Sélection de vue — M/T/A sur mobile */}
      <div className="flex gap-1 sm:border-r sm:pr-3">
        <button onClick={() => setView("mensuel")}
          className={`flex-1 sm:flex-none px-2 sm:px-3 py-1 text-xs rounded ${view === "mensuel" ? "bg-blue-600 text-white" : "hover:bg-slate-100"}`}>
          <span className="sm:hidden">M</span><span className="hidden sm:inline">Mensuel</span>
        </button>
        <button onClick={() => setView("trimestriel")}
          className={`flex-1 sm:flex-none px-2 sm:px-3 py-1 text-xs rounded ${view === "trimestriel" ? "bg-blue-600 text-white" : "hover:bg-slate-100"}`}>
          <span className="sm:hidden">T</span><span className="hidden sm:inline">Trimestriel</span>
        </button>
        <button onClick={() => setView("annuel")}
          className={`flex-1 sm:flex-none px-2 sm:px-3 py-1 text-xs rounded ${view === "annuel" ? "bg-blue-600 text-white" : "hover:bg-slate-100"}`}>
          <span className="sm:hidden">A</span><span className="hidden sm:inline">Annuel</span>
        </button>
      </div>

      {/* Sélecteurs — sur 1 ligne sur mobile */}
      <div className="flex gap-2">
        {view === "mensuel" && (
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="w-[120px] sm:w-32 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.slice(1).map((m, idx) => (
                <SelectItem key={idx + 1} value={String(idx + 1)}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {view === "trimestriel" && (
          <Select value={String(quarter || 1)} onValueChange={(v) => setQuarter(Number(v))}>
            <SelectTrigger className="w-[120px] sm:w-36 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {QUARTERS.map(q => (
                <SelectItem key={q.id} value={String(q.id)}>{q.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-[80px] sm:w-24 h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {years.map(y => (
              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export const KPICard = ({ title, value, subtitle, change, changeType, icon: Icon, color = "emerald", targetConfig, previousValue, description, onClick, kpiKey }) => {
  const colorClasses = {
    emerald: "bg-emerald-100 text-emerald-600",
    amber: "bg-amber-100 text-amber-600",
    red: "bg-red-100 text-red-500",
    blue: "bg-blue-100 text-blue-600",
    purple: "bg-purple-100 text-purple-600",
    green: "bg-emerald-100 text-emerald-600",
    orange: "bg-amber-100 text-amber-600",
    gray: "bg-slate-100 text-slate-600"
  };

  // Déterminer la couleur dynamique basée sur les cibles
  let dynamicColor = color;
  if (targetConfig && value !== null && value !== undefined) {
    const numValue = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
    if (!isNaN(numValue)) {
      dynamicColor = getStatusColor(numValue, targetConfig);
    }
  }

  // Calculer la tendance
  const trend = previousValue !== undefined ? getTrendArrow(
    parseFloat(String(value).replace(/[^0-9.-]/g, '')), 
    previousValue
  ) : null;

  const cardContent = (
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <p className="kpi-label text-slate-500">{title}</p>
              {description && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Dialog>
                        <DialogTrigger asChild>
                          <button className="text-slate-400 hover:text-slate-600 transition-colors">
                            <HelpCircle className="w-4 h-4" />
                          </button>
                        </DialogTrigger>
                        <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle className="text-lg font-heading">{title}</DialogTitle>
                          </DialogHeader>
                          <div className="prose prose-sm text-slate-600 whitespace-pre-wrap leading-relaxed">
                            {description}
                          </div>
                          {targetConfig && (
                            <div className="mt-4 pt-4 border-t border-slate-200">
                              <p className="font-medium text-sm mb-2">Seuils de performance:</p>
                              <div className="flex gap-3 text-sm">
                                <span className="flex items-center gap-1">
                                  <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
                                  Vert: {targetConfig.direction === "higher_is_better" ? "≥" : "≤"} {targetConfig.thresholds.green}{targetConfig.unit}
                                </span>
                                <span className="flex items-center gap-1">
                                  <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                                  Orange: {targetConfig.direction === "higher_is_better" ? "≥" : "≤"} {targetConfig.thresholds.orange}{targetConfig.unit}
                                </span>
                                <span className="flex items-center gap-1">
                                  <span className="w-3 h-3 rounded-full bg-red-500"></span>
                                  Rouge: sinon
                                </span>
                              </div>
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p>Cliquer pour plus de détails</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <div className="flex items-center gap-2">
              <p className="kpi-value text-2xl text-slate-900">{value}</p>
              {trend && (
                <TrendIndicator trend={trend} />
              )}
            </div>
            {subtitle && (
              <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
            )}
            {change !== undefined && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${changeType === 'positive' ? 'text-emerald-600' : changeType === 'negative' ? 'text-red-500' : 'text-slate-500'}`}>
                <span>{change}</span>
              </div>
            )}
          </div>
          {Icon && (
            <div className={`p-2.5 rounded-xl ${colorClasses[dynamicColor] || colorClasses[color]}`}>
              <Icon className="w-5 h-5" />
            </div>
          )}
        </div>
      </CardContent>
  );

  // Si onClick est fourni, rendre la carte cliquable
  if (onClick && kpiKey) {
    return (
      <Card 
        className="card-hover cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02]" 
        onClick={onClick}
      >
        {cardContent}
        <div className="absolute bottom-2 right-2 text-xs text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity">
          Cliquer pour voir l'évolution
        </div>
      </Card>
    );
  }

  return (
    <Card className="card-hover">
      {cardContent}
    </Card>
  );
};

// Composant indicateur de tendance
export const TrendIndicator = ({ trend }) => {
  if (!trend) return null;
  
  const { direction, value } = trend;
  
  if (direction === "up") {
    return (
      <span className="inline-flex items-center gap-0.5 text-sm text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">
        <ArrowUpRight className="w-3.5 h-3.5" />
        <span className="text-xs font-medium">+{value.toFixed(1)}%</span>
      </span>
    );
  }
  
  if (direction === "down") {
    return (
      <span className="inline-flex items-center gap-0.5 text-sm text-red-500 bg-red-50 px-1.5 py-0.5 rounded-full">
        <ArrowDownRight className="w-3.5 h-3.5" />
        <span className="text-xs font-medium">{value.toFixed(1)}%</span>
      </span>
    );
  }
  
  return (
    <span className="inline-flex items-center gap-0.5 text-sm text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full">
      <Minus className="w-3.5 h-3.5" />
      <span className="text-xs font-medium">0%</span>
    </span>
  );
};

export const DataTable = ({ columns, data, emptyMessage = "Aucune donnée" }) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-200">
            {columns.map((col, idx) => (
              <th 
                key={col.id || col._id || idx} 
                className={`py-3 px-4 kpi-label text-slate-500 ${col.align === 'right' ? 'text-right' : 'text-left'}`}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIdx) => (
            <tr key={rowIdx} className="table-row-hover border-b border-slate-100">
              {columns.map((col, colIdx) => (
                <td 
                  key={colIdx} 
                  className={`py-3 px-4 ${col.className || ''} ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                >
                  {col.render ? col.render(row[col.key], row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const IndicatorRow = ({ label, value, target, unit = "", isGood = true, targetConfig, description }) => {
  // Utiliser targetConfig si fourni, sinon utiliser la logique simple
  const getStatusColorClass = () => {
    if (targetConfig && value !== null && value !== undefined) {
      const numValue = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
      if (!isNaN(numValue)) {
        const status = getStatusColor(numValue, targetConfig);
        if (status === "green") return "text-emerald-600";
        if (status === "orange") return "text-amber-600";
        if (status === "red") return "text-red-500";
      }
    }
    
    // Fallback logique simple
    if (target === undefined) return "text-slate-900";
    const numValue = parseFloat(value);
    const numTarget = parseFloat(target);
    if (isGood) {
      return numValue >= numTarget ? "text-emerald-600" : "text-red-500";
    } else {
      return numValue <= numTarget ? "text-emerald-600" : "text-red-500";
    }
  };

  // Déterminer le badge de couleur
  const getStatusBadge = () => {
    if (!targetConfig) return null;
    const numValue = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
    if (isNaN(numValue)) return null;
    const status = getStatusColor(numValue, targetConfig);
    const colors = {
      green: "bg-emerald-500",
      orange: "bg-amber-500",
      red: "bg-red-500",
      gray: "bg-slate-400"
    };
    return <span className={`w-2 h-2 rounded-full ${colors[status]}`}></span>;
  };

  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-2">
        {getStatusBadge()}
        <span className="text-sm text-slate-600">{label}</span>
        {description && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Dialog>
                  <DialogTrigger asChild>
                    <button className="text-slate-400 hover:text-slate-600 transition-colors">
                      <HelpCircle className="w-3.5 h-3.5" />
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-lg font-heading">{label}</DialogTitle>
                    </DialogHeader>
                    <div className="prose prose-sm text-slate-600 whitespace-pre-wrap leading-relaxed">
                      {description}
                    </div>
                    {targetConfig && (
                      <div className="mt-4 pt-4 border-t border-slate-200">
                        <p className="font-medium text-sm mb-2">Seuils:</p>
                        <div className="flex gap-3 text-sm">
                          <span className="flex items-center gap-1">
                            <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
                            Vert: {targetConfig.direction === "higher_is_better" ? "≥" : "≤"} {targetConfig.thresholds.green}{targetConfig.unit || unit}
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                            Orange: {targetConfig.direction === "higher_is_better" ? "≥" : "≤"} {targetConfig.thresholds.orange}{targetConfig.unit || unit}
                          </span>
                        </div>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>Cliquer pour plus de détails</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <div className="flex items-center gap-3">
        <span className={`money-value font-medium ${getStatusColorClass()}`}>
          {value}{unit}
        </span>
        {target !== undefined && !targetConfig && (
          <span className="text-xs text-slate-400">
            Cible: {target}{unit}
          </span>
        )}
      </div>
    </div>
  );
};

export const DepartmentBadge = ({ dept }) => {
  const colors = {
    "PRQC": "bg-emerald-100 text-emerald-700",
    "LMDS": "bg-blue-100 text-blue-700",
    "LOCATION": "bg-purple-100 text-purple-700",
    "GLOBAL": "bg-slate-100 text-slate-700"
  };

  const names = {
    "PRQC": "Peinture",
    "LMDS": "Stores",
    "LOCATION": "Location",
    "GLOBAL": "Global"
  };

  return (
    <span className={`text-xs px-2 py-1 rounded-full font-medium ${colors[dept] || colors.GLOBAL}`}>
      {names[dept] || dept}
    </span>
  );
};
